import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import apiService from '../services/api';
import { OnboardingBanner } from '../components/onboarding/OnboardingBanner';
import { UserDashboardFiltersDto, WorkOrderDto, PriorityDto, User, StatusDto } from '../types';
import UserStatsCards from '../components/dashboard/UserStatsCards';
import { TrendingUp, CheckCircle, Clock, Plus, Search, ChevronUp, ChevronDown, Eye, Edit, Trash2, Printer, Mail, AlertTriangle, DollarSign, Calendar } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useDebounce } from '../hooks/useDebounce';
import { useInvoiceHealthCheck } from '../hooks/useInvoiceHealthCheck';
import Modal from '../components/ui/Modal';
import { Button } from '../components/ui/button';
import ConfirmDialog from '../components/modals/ConfirmDialog';
import toast from 'react-hot-toast';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isSystemAdmin = typeof window !== 'undefined' && localStorage.getItem('isSystemAdmin') === 'true';
  const isSuperAdmin = isSystemAdmin || user?.roleName === 'SuperAdmin';
  
  // Modern filtering state
  const [search, setSearch] = useState('');
  const [assignedToFilter, setAssignedToFilter] = useState<string>(user?.id?.toString() || 'all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<string>('my-open');
  const [sortField, setSortField] = useState<string>('dueDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  
  const debouncedSearch = useDebounce(search, 300);

  // Email modal state
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [selectedWorkOrderForEmail, setSelectedWorkOrderForEmail] = useState<WorkOrderDto | null>(null);
  const [recipientEmail, setRecipientEmail] = useState('');

  // Delete confirmation state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [workOrderToDelete, setWorkOrderToDelete] = useState<WorkOrderDto | null>(null);

  // Fetch data for modern dashboard
  const { data: prioritiesData } = useQuery({
    queryKey: ['priorities'],
    queryFn: () => apiService.getPriorities(),
    staleTime: 5 * 60_000
  });

  const { data: usersData } = useQuery({
    queryKey: ['company-users', 'dashboard-filters'],
    queryFn: () => apiService.getCompanyUsers(),
    staleTime: 5 * 60_000
  });

  const { data: statusesData } = useQuery({
    queryKey: ['statuses'],
    queryFn: () => apiService.getStatuses(),
    staleTime: 5 * 60_000
  });

  const { data: clientsData } = useQuery({
    queryKey: ['clients', 'dashboard-filters'],
    queryFn: () => apiService.getClients(1, 500),
    staleTime: 5 * 60_000
  });

  // Fetch user dashboard stats
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['user-dashboard-stats'],
    queryFn: () => apiService.getUserDashboardStats(),
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });

  // Fetch invoice health check
  const { data: invoiceHealthData, isLoading: invoiceHealthLoading } = useInvoiceHealthCheck();

  // Fetch upcoming PM schedules
  const { data: upcomingPMResponse, isLoading: upcomingPMLoading } = useQuery({
    queryKey: ['upcoming-pm-schedules'],
    queryFn: () => apiService.getUpcomingMaintenanceSchedules(7), // Next 7 days
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Show toast notification for invoice health issues (skip for superadmins)
  React.useEffect(() => {
    if (!isSuperAdmin && invoiceHealthData && !invoiceHealthLoading) {
      if (invoiceHealthData.overdueCount > 0) {
        toast.error(`You have ${invoiceHealthData.overdueCount} overdue invoice${invoiceHealthData.overdueCount === 1 ? '' : 's'}`, {
          duration: 6000,
          icon: '⚠️',
        });
      } else if (invoiceHealthData.upcomingDueCount > 0) {
        toast(`You have ${invoiceHealthData.upcomingDueCount} invoice${invoiceHealthData.upcomingDueCount === 1 ? '' : 's'} due within 7 days`, {
          duration: 4000,
          icon: '📅',
        });
      }
    }
  }, [invoiceHealthData, invoiceHealthLoading, isSuperAdmin]);

  const { data: tabCountsResponse, isLoading: tabCountsLoading } = useQuery({
    queryKey: ['dashboard-workorder-tab-counts'],
    queryFn: () => apiService.getWorkOrderTabCounts(),
    staleTime: 60_000
  });

  // Fetch work orders with modern filtering (paginated for display)
  const { data: workOrdersResp, isLoading: workOrdersLoading, isError: workOrdersError } = useQuery({
    queryKey: ['dashboard-workorders', { page, pageSize, search: debouncedSearch, priorityFilter, sortField, sortDirection, activeTab }],
    queryFn: async () => {
      const res = await apiService.getDashboardWorkOrders({
        tabFilter: activeTab,
        searchTerm: debouncedSearch || undefined,
        priorityId: priorityFilter && priorityFilter !== 'all' ? parseInt(priorityFilter) : undefined,
        page,
        pageSize,
        sortBy: sortField,
        sortDirection,
      });
      return res;
    },
    keepPreviousData: true
  });

  // Mutations to match WorkOrders hover actions
  const deleteWorkOrderMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteWorkOrder(id),
    onSuccess: () => {
      toast.success('Work order deleted successfully');
      setIsDeleteModalOpen(false);
      setWorkOrderToDelete(null);
      // Invalidate queries to refresh the dashboard
      queryClient.invalidateQueries({ queryKey: ['dashboard-workorders'] });
      queryClient.invalidateQueries({ queryKey: ['workorders-v2'] });
    },
    onError: (error: any) => {
      console.error('Error deleting work order:', error);
      toast.error('Failed to delete work order');
    }
  });

  const handleDeleteClick = (workOrder: WorkOrderDto) => {
    setWorkOrderToDelete(workOrder);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = () => {
    if (workOrderToDelete) {
      deleteWorkOrderMutation.mutate(workOrderToDelete.id);
    }
  };

  const renderWorkOrderMutation = useMutation({
    mutationFn: (workOrderId: number) => apiService.renderWorkOrder(workOrderId),
    onSuccess: (response) => {
      try {
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.open();
          printWindow.document.write(response.data);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
        }
      } catch (e) {
        toast.error('Failed to open print preview');
      }
    },
    onError: (error: any) => {
      console.error('Error rendering work order:', error);
      toast.error('Failed to render work order');
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: ({ workOrderId, email, message }: { workOrderId: number; email: string; message?: string }) => 
      apiService.sendWorkOrderEmail(workOrderId, email, message),
    onSuccess: () => {
      toast.success('Email sent');
      setIsEmailModalOpen(false);
      setSelectedWorkOrderForEmail(null);
      setRecipientEmail('');
    },
    onError: (error: any) => {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    }
  });


  const stats = statsResponse?.data || {
    myOpenWorkOrders: 0,
    workOrdersDueToday: 0,
    overdueWorkOrders: 0,
    workOrdersCompletedThisMonth: 0,
    averageDaysToComplete: 0,
    totalWorkOrdersAssigned: 0,
    workOrdersInProgress: 0,
    workOrdersAssigned: 0,
    workOrdersCompleted: 0,
    workOrdersCancelled: 0,
    performanceMetrics: {
      completedThisMonth: 0,
      completedLastMonth: 0,
      completionTrend: 0,
      averageCompletionTime: 0,
      onTimeCompletions: 0,
      totalCompletions: 0,
      onTimeCompletionRate: 0
    }
  };

  // Process data with useMemo to prevent unnecessary recalculations
  const priorities: PriorityDto[] = useMemo(() => 
    (prioritiesData as any)?.data || prioritiesData || [], 
    [prioritiesData]
  );
  
  const users: User[] = useMemo(() =>
    ((usersData as any)?.data || []) as User[],
    [usersData]
  );

  const assignedToOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'All Users' }];
    if (user?.id) {
      options.push({
        value: user.id.toString(),
        label: `Me (${user.firstName} ${user.lastName})`.trim(),
      });
    }
    users
      .filter((u) => u.id && u.id !== user?.id)
      .forEach((u) => {
        options.push({
          value: u.id.toString(),
          label: `${u.firstName} ${u.lastName}`.trim() || u.email || 'Unknown user',
        });
      });
    return options;
  }, [users, user?.id, user?.firstName, user?.lastName]);
  
  const statuses: StatusDto[] = useMemo(() => 
    (statusesData as any)?.data || statusesData || [], 
    [statusesData]
  );
  
  const clients = useMemo(() => 
    (clientsData as any)?.data?.data || [], 
    [clientsData]
  );
  
  const workOrdersLoadFailed = workOrdersError || workOrdersResp?.success === false;

  const { items, total } = useMemo(() => {
    const payload = workOrdersResp?.data as any;
    const pagedItems = payload?.data ?? payload?.Data;
    const workOrderItems: WorkOrderDto[] = Array.isArray(pagedItems)
      ? pagedItems
      : Array.isArray(payload)
        ? payload
        : [];
    const workOrderTotal = payload?.totalCount ?? payload?.TotalCount ?? workOrderItems.length;
    return { items: workOrderItems, total: workOrderTotal };
  }, [workOrdersResp]);

  const tabCounts = useMemo(
    () => tabCountsResponse?.data?.counts ?? {},
    [tabCountsResponse]
  );

  // Helper functions with useCallback to prevent unnecessary re-renders
  const daysRemaining = useCallback((due?: string | null) => {
    if (!due) return null;
    const d = new Date(due).setHours(0,0,0,0);
    const today = new Date().setHours(0,0,0,0);
    const diff = Math.round((d - today) / 86400000);
    return diff;
  }, []);

  const daysColorClass = useCallback((d: number | null) => {
    if (d == null) return '';
    if (d < 0) return 'text-red-600 dark:text-red-400 font-semibold';
    if (d <= 2) return 'text-red-600 dark:text-red-400 font-semibold';
    if (d <= 6) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-700 dark:text-green-300';
  }, []);

  const priorityColor = useCallback((id?: number) => {
    if (!id) return '#9ca3af';
    const p = priorities.find(x => x.id === id);
    return p?.colorCode || '#9ca3af';
  }, [priorities]);

  const avatarColor = useCallback((name?: string) => {
    if (!name) return '#6b7280';
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 60%, 65%)`;
  }, []);

  const gotoWO = useCallback((id: number) => navigate(`/workorders/${id}/contact?from=dashboard`), [navigate]);

  const handleSort = useCallback((field: string) => {
    let nextDirection: 'asc' | 'desc' = 'asc';
    if (sortField === field) {
      nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortDirection(nextDirection);
  }, [sortField, sortDirection]);

  const getSortIcon = useCallback((field: string) => {
    const isActive = sortField === field;
    return (
      <span className="inline-flex items-center w-4 h-4 ml-1">
        {isActive && (sortDirection === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />)}
      </span>
    );
  }, [sortField, sortDirection]);

  // Memoized event handlers
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleAssignedToChange = useCallback((value: string) => {
    setAssignedToFilter(value);
  }, []);

  const handlePriorityChange = useCallback((value: string) => {
    setPriorityFilter(value);
  }, []);

  const handleClientChange = useCallback((value: string) => {
    setClientFilter(value);
  }, []);

  const handleDateChange = useCallback((value: string) => {
    setDateFilter(value);
  }, []);

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setPage(1);
    if (tab === 'my-open') {
      setAssignedToFilter(user?.id?.toString() || 'all');
    } else {
      setAssignedToFilter('all');
    }
  }, [user?.id]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);


  return (
    <div className="space-y-6 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Onboarding Banner */}
      <OnboardingBanner />

      {/* Statistics Cards */}
      <UserStatsCards
        stats={stats}
        isLoading={statsLoading || tabCountsLoading}
        activeFilter={activeTab}
        onStatClick={handleTabChange}
        tabCounts={tabCountsLoading ? undefined : tabCounts}
        statuses={statuses}
        userId={user?.id}
      />

      {/* Upcoming PM Schedules Widget */}
      {upcomingPMResponse?.data && upcomingPMResponse.data.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-accent-color" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upcoming PM Schedules</h2>
            </div>
            <Link
              to="/maintenance-schedules"
              className="text-sm text-accent-color hover:underline"
            >
              View All
            </Link>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {upcomingPMResponse.data.slice(0, 5).map((schedule: any) => (
              <div
                key={schedule.id}
                className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors cursor-pointer"
                onClick={() => navigate(`/equipment/${schedule.equipmentId}`)}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-gray-900 dark:text-gray-100">{schedule.name}</span>
                    {schedule.isOverdue && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                        Overdue
                      </span>
                    )}
                    {!schedule.isOverdue && schedule.daysUntilDue <= 2 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        Due Soon
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{schedule.equipmentName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {schedule.isOverdue
                      ? `${Math.abs(schedule.daysUntilDue)} days overdue`
                      : `Due in ${schedule.daysUntilDue} day${schedule.daysUntilDue !== 1 ? 's' : ''}`}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {new Date(schedule.nextDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Invoice Health Check Banner (skip for superadmins) */}
      {!isSuperAdmin && invoiceHealthData && (invoiceHealthData.overdueCount > 0 || invoiceHealthData.upcomingDueCount > 0) && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  Invoice Health Alert
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  {invoiceHealthData.overdueCount > 0 && (
                    <span>{invoiceHealthData.overdueCount} overdue invoice{invoiceHealthData.overdueCount === 1 ? '' : 's'}</span>
                  )}
                  {invoiceHealthData.overdueCount > 0 && invoiceHealthData.upcomingDueCount > 0 && ' • '}
                  {invoiceHealthData.upcomingDueCount > 0 && (
                    <span>{invoiceHealthData.upcomingDueCount} due within 7 days</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/invoices')}
                className="text-amber-700 border-amber-300 hover:bg-amber-100 dark:text-amber-300 dark:border-amber-700 dark:hover:bg-amber-900/20"
              >
                <DollarSign className="h-4 w-4 mr-1" />
                View Invoices
              </Button>
            </div>
          </div>
          
          {/* Show specific issues */}
          {invoiceHealthData.issues.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
              <div className="space-y-2">
                {invoiceHealthData.issues.slice(0, 3).map((issue) => (
                  <div key={issue.invoiceId} className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        issue.issueType === 'Overdue' 
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                      }`}>
                        {issue.issueType === 'Overdue' ? 'Overdue' : 'Due Soon'}
                      </span>
                      <span className="text-gray-700 dark:text-gray-300">
                        Invoice #{issue.invoiceNumber} - {issue.clientName}
                      </span>
                    </div>
                    <div className="text-gray-600 dark:text-gray-400">
                      ${issue.amountDue.toFixed(2)}
                    </div>
                  </div>
                ))}
                {invoiceHealthData.issues.length > 3 && (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    +{invoiceHealthData.issues.length - 3} more invoice{invoiceHealthData.issues.length - 3 === 1 ? '' : 's'}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search by WO#, client, city, vendor..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
            aria-label="Search work orders by number, client, or status"
          />
        </div>
        
        <SearchableSelect
          className="w-56"
          options={assignedToOptions}
          value={assignedToFilter || 'all'}
          onChange={(value) => handleAssignedToChange(String(value || 'all'))}
          placeholder="Assigned to"
          searchPlaceholder="Search users..."
          allowClear={false}
          aria-label="Filter by assigned user"
        />
        
        <Select value={priorityFilter} onValueChange={handlePriorityChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            {priorities.map(priority => (
              <SelectItem key={priority.id} value={priority.id.toString()}>
                {priority.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={clientFilter} onValueChange={handleClientChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Clients</SelectItem>
            {clients.map(client => (
              <SelectItem key={client.id} value={client.id.toString()}>
                {client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={dateFilter} onValueChange={handleDateChange}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Date" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any Date</SelectItem>
            <SelectItem value="today">Today</SelectItem>
            <SelectItem value="week">This Week</SelectItem>
            <SelectItem value="month">This Month</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Modern Compact Grid */}
      <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="w-24 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('workOrderNumber')}>
                  <div className="flex items-center">
                    WO#
                    {getSortIcon('workOrderNumber')}
                  </div>
                </th>
                <th className="w-52 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('clientName')}>
                  <div className="flex items-center">
                    Client/Location
                    {getSortIcon('clientName')}
                  </div>
                </th>
                <th className="w-44 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('vendorName')}>
                  <div className="flex items-center">
                    Vendor
                    {getSortIcon('vendorName')}
                  </div>
                </th>
                <th className="w-28 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('statusName')}>
                  <div className="flex items-center">
                    Status
                    {getSortIcon('statusName')}
                  </div>
                </th>
                <th className="w-20 px-2 py-1 text-center text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Priority
                </th>
                <th className="w-28 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('dueDate')}>
                  <div className="flex items-center">
                    Due Date
                    {getSortIcon('dueDate')}
                  </div>
                </th>
                <th className="w-32 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Days Remaining
                </th>
                <th className="w-28 px-2 py-1 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800" onClick={() => handleSort('assignedUserName')}> 
                  <div className="flex items-center">
                    Assigned To
                    {getSortIcon('assignedUserName')}
                  </div>
                </th>
                {/* Actions column removed - hover actions integrated into Assigned To */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {workOrdersLoading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : workOrdersLoadFailed ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <div className="text-red-600 dark:text-red-400 font-medium">Error loading work orders</div>
                    <div className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
                      {workOrdersResp?.message || 'The server could not load work orders. If this started after a deploy, run database migrations against the Azure production connection string.'}
                    </div>
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No work orders found</td></tr>
              ) : items.map(w => {
                const d = daysRemaining(w.dueDate);
                return (
                  <tr key={w.id}
                    className="group hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                    onClick={() => gotoWO(w.id)}
                  >
                    {/* WO# */}
                    <td className="px-2 py-1">
                      <div className="text-[14px] font-semibold">
                        <span className="text-blue-700 dark:text-blue-400 hover:underline" onClick={(e)=>{e.stopPropagation(); gotoWO(w.id);}} role="link" tabIndex={0} onKeyDown={(e)=>{if(e.key==='Enter'){e.stopPropagation(); gotoWO(w.id);}}} aria-label={`Open work order ${w.workOrderNumber}`}>
                          {w.workOrderNumber}
                        </span>
                      </div>
                    </td>
                    {/* Client/Location */}
                    <td className="px-2 py-1">
                      <div className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">{w.clientName}</div>
                      <div className="text-[12px] text-gray-600 dark:text-gray-400">{[w.clientCity, w.clientState].filter(Boolean).join(', ')}</div>
                    </td>
                    {/* Vendor */}
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[12px]" style={{backgroundColor: avatarColor(w.vendors?.[0]?.vendorName)}}>
                          {(w.vendors?.[0]?.vendorName || 'U').split(' ').map(s=>s[0]).slice(0,2).join('')}
                        </div>
                        <div className="text-[13px] text-gray-800 dark:text-gray-200">{w.vendors?.[0]?.vendorName || 'Unassigned'}</div>
                      </div>
                    </td>
                    {/* Status */}
                    <td className="px-2 py-1">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        w.statusName === 'Open' ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' :
                        w.statusName === 'In Progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                        w.statusName === 'Pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                        w.statusName === 'On Hold' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                        w.statusName === 'Completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                      }`}>
                        {w.statusName || 'Unknown'}
                      </span>
                    </td>
                    {/* Priority */}
                    <td className="px-2 py-1">
                      <div className="flex items-center justify-center">
                        <div title={w.priorityName || ''} aria-label={`Priority ${w.priorityName || ''}`} className="h-7 w-7 rounded-full flex items-center justify-center text-white text-[12px] font-semibold" style={{backgroundColor: priorityColor(w.priorityId)}}>
                          {(w.priorityName || 'U').charAt(0).toUpperCase()}
                        </div>
                      </div>
                    </td>
                    {/* Due Date */}
                    <td className="px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200">
                      {w.dueDate ? new Date(w.dueDate).toLocaleDateString() : ''}
                    </td>
                    {/* Days Remaining */}
                    <td className={`px-2 py-1 text-[13px] ${daysColorClass(d)}`}>
                      {d==null? '' : d<0 ? `${Math.abs(d)} days overdue` : `${d} days left`}
                    </td>
                    {/* Assigned To - overlay identical to WorkOrders grid */}
                    <td className="px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200 relative">
                      <div className="group-hover:opacity-0 transition-opacity duration-200">
                        {w.assignedUserName || 'System'}
                      </div>
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-end gap-2 pr-2" onClick={(e)=>e.stopPropagation()}>
                        <Button variant="outline" size="sm" onClick={()=>gotoWO(w.id)} className="h-7 px-2 !bg-white dark:!bg-gray-800 !border-gray-300 dark:!border-gray-600 !text-gray-700 dark:!text-gray-300 hover:!bg-gray-50 dark:hover:!bg-gray-600" title="View Job Contact Sheet">
                          <span className="inline-flex items-center justify-center h-4 w-4 bg-gray-100 dark:bg-gray-700 rounded">
                            <Eye className="h-3.5 w-3.5"/>
                          </span>
                        </Button>
                        <Link to={`/workorders/${w.id}/edit`} className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600" title="Edit Work Order">
                          <Edit className="h-4 w-4"/>
                        </Link>
                        <button
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={async (e)=>{
                            e.stopPropagation();
                            try {
                              const response = await apiService.renderWorkOrder(w.id);
                              const blob = new Blob([response.data], { type: 'text/html' });
                              const url = window.URL.createObjectURL(blob);
                              const printWindow = window.open(url, '_blank');
                              if (printWindow) {
                                printWindow.onload = () => {
                                  printWindow.print();
                                  window.URL.revokeObjectURL(url);
                                };
                              }
                            } catch (error:any) {
                              console.error('Error printing work order:', error);
                              toast.error(`Error printing work order: ${error?.response?.data?.message || error.message}`);
                            }
                          }}
                          title="Print Work Order"
                        >
                          <Printer className="h-4 w-4"/>
                        </button>
                        <button
                          className="h-7 px-2 inline-flex items-center border rounded-md text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                          onClick={(e)=>{ e.stopPropagation(); setSelectedWorkOrderForEmail(w); setIsEmailModalOpen(true); }}
                          title="Email Work Order"
                        >
                          <Mail className="h-4 w-4"/>
                        </button>
                        <button className="h-7 px-2 inline-flex items-center border rounded-md text-red-600 border-red-300 hover:bg-red-50" onClick={(e)=>{e.stopPropagation(); handleDeleteClick(w);}} title="Delete Work Order">
                          <Trash2 className="h-4 w-4"/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Simple pagination */}
      <div className="flex items-center justify-between text-sm">
        <div className="text-gray-600 dark:text-gray-400">Page {page} • {items.length} of {total} total</div>
        <div className="space-x-2">
          <button 
            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={()=>setPage(p=>Math.max(1,p-1))} 
            disabled={page===1}
          >
            Prev
          </button>
          <button 
            className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={()=>setPage(p=>p+1)} 
            disabled={page * pageSize >= total}
          >
            Next
          </button>
        </div>
      </div>
      {stats?.performanceMetrics && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">Performance Metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">On-Time Rate</span>
              </div>
              <div className="text-xl font-bold text-green-600">
                {(stats.performanceMetrics.onTimeCompletionRate || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {stats.performanceMetrics.onTimeCompletions || 0}/{stats.performanceMetrics.totalCompletions || 0} on time
              </div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <Clock className="h-4 w-4 text-accent-color" />
                <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">Avg Time</span>
              </div>
              <div className="text-xl font-bold text-accent-color">
                {(stats.performanceMetrics.averageCompletionTime || 0).toFixed(1)}d
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">completion time</div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <TrendingUp className={`h-4 w-4 ${
                  (stats.performanceMetrics.completionTrend || 0) > 0 ? 'text-green-500' : 
                  (stats.performanceMetrics.completionTrend || 0) < 0 ? 'text-red-500 rotate-180' : 'text-gray-500'
                }`} />
                <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">Trend</span>
              </div>
              <div className={`text-xl font-bold ${
                (stats.performanceMetrics.completionTrend || 0) > 0 ? 'text-green-600' : 
                (stats.performanceMetrics.completionTrend || 0) < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                {(stats.performanceMetrics.completionTrend || 0) > 0 ? '+' : ''}{(stats.performanceMetrics.completionTrend || 0).toFixed(1)}%
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">vs last month</div>
            </div>
            
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <CheckCircle className="h-4 w-4 text-purple-500" />
                <span className="ml-1 text-sm font-medium text-gray-600 dark:text-gray-300">This Month</span>
              </div>
              <div className="text-xl font-bold text-purple-600">
                {stats.workOrdersCompletedThisMonth || 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                completed ({stats.performanceMetrics.completedLastMonth || 0} last)
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Button for New Work Order */}
      <button
        onClick={() => navigate('/workorders/create')}
        className="fixed bottom-6 right-6 z-50 bg-accent-color hover:bg-accent-color-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-accent-color-100"
        title="Create New Work Order"
      >
        <Plus className="h-6 w-6" />
      </button>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setWorkOrderToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Work Order"
        message={`Are you sure you want to delete work order "${workOrderToDelete?.workOrderNumberFormatted || workOrderToDelete?.workOrderNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        isLoading={deleteWorkOrderMutation.isPending}
      />
    </div>
  );
};

export default DashboardPage;
